import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Car, User as UserIcon, Pencil, Trash2, ClipboardCheck, AlertCircle, Send, Archive, Package } from "lucide-react";
import { api } from "@/lib/apiClient";
import EditarVeiculoClienteDialog from "@/components/EditarVeiculoClienteDialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

/**
 * Reusable Kanban board with drag & drop + inline edit/delete.
 *
 * Props:
 *  - columns: [{ id, label, accent }]
 *  - jobs: array (filtered by job_type by parent)
 *  - onStatusChange: async (jobId, newStatusId) => any
 *  - renderCardExtra?: (job) => JSX (mini info like seguradora/claim)
 *  - onJobsChanged?: () => void — chamado após edit/delete para o parent recarregar
 */
export default function KanbanBoard({ columns, jobs, onStatusChange, renderCardExtra, onJobsChanged }) {
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const [localJobs, setLocalJobs] = useState(jobs);
  const [editJobId, setEditJobId] = useState(null);
  const [confirmDelJob, setConfirmDelJob] = useState(null);
  const [openingInspection, setOpeningInspection] = useState(null);
  const [moveJob, setMoveJob] = useState(null);
  const [moving, setMoving] = useState(false);
  const [archiveJob, setArchiveJob] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => setLocalJobs(jobs), [jobs]);

  const openInspection = async (jobId) => {
    setOpeningInspection(jobId);
    try {
      const { data } = await api.get(`/jobs/${jobId}/inspection`);
      const inspId = data?.inspection?.id || data?.id;
      if (inspId) {
        navigate(`/inspecoes/${inspId}`);
      } else {
        toast.error("Não foi possível abrir a inspeção");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao abrir inspeção");
    } finally {
      setOpeningInspection(null);
    }
  };

  const onDragStart = (id) => (e) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
  };

  const onDrop = (colId) => async (e) => {
    e.preventDefault();
    setOverCol(null);
    if (!dragId) return;
    const job = localJobs.find((j) => j.id === dragId);
    if (!job || job.status === colId) { setDragId(null); return; }
    setLocalJobs((prev) => prev.map((j) => j.id === dragId ? { ...j, status: colId } : j));
    setDragId(null);
    try {
      await onStatusChange(job.id, colId);
      toast.success(`Movido para ${columns.find((c) => c.id === colId).label}`);
    } catch {
      toast.error("Falha ao atualizar status");
    }
  };

  const deleteJob = async () => {
    if (!confirmDelJob) return;
    try {
      await api.delete(`/jobs/${confirmDelJob.id}`);
      toast.success("OS removida");
      setConfirmDelJob(null);
      onJobsChanged && onJobsChanged();
    } catch {
      toast.error("Erro ao remover OS");
    }
  };

  return (
    <div className="flex gap-3 overflow-x-auto pb-4 custom-scroll snap-x-mobile" data-testid="kanban-board">
      {columns.map((col) => {
        const colJobs = localJobs.filter((j) => j.status === col.id);
        return (
          <div
            key={col.id}
            className={`shrink-0 w-[85vw] max-w-[300px] md:w-[300px] border border-border rounded-md bg-card flex flex-col max-h-[calc(100vh-220px)] transition-colors ${overCol === col.id ? "ring-2 ring-primary" : ""}`}
            onDragOver={(e) => { e.preventDefault(); setOverCol(col.id); }}
            onDragLeave={() => setOverCol((c) => c === col.id ? null : c)}
            onDrop={onDrop(col.id)}
            data-testid={`kanban-col-${col.id}`}
          >
            <div className="px-3 py-2.5 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${col.accent}`} />
                <div className="text-xs font-semibold uppercase tracking-wider truncate">{col.label}</div>
              </div>
              <div className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground font-mono">{colJobs.length}</div>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-2 custom-scroll">
              {colJobs.map((j) => {
                const priorityStyle = {
                  urgente: { bar: "bg-red-500", text: "text-red-500", label: "Urgente" },
                  importante: { bar: "bg-orange-500", text: "text-orange-500", label: "Importante" },
                  monitorar: { bar: "bg-neutral-500", text: "text-muted-foreground", label: "Monitorar" },
                }[j.priority];
                return (
                <div
                  key={j.id}
                  draggable
                  onDragStart={onDragStart(j.id)}
                  className={`group border border-border rounded-md bg-background overflow-hidden cursor-grab active:cursor-grabbing hover:-translate-y-0.5 hover:shadow-sm transition-all relative ${dragId === j.id ? "dragging" : ""}`}
                  data-testid={`kanban-card-${j.id}`}
                >
                  {priorityStyle && (
                    <div className={`absolute top-0 left-0 bottom-0 w-1 ${priorityStyle.bar} z-10`} />
                  )}
                  <div className="h-24 bg-secondary relative">
                    {j.vehicle?.photo_url ?
                      <img src={j.vehicle.photo_url} alt="" className="w-full h-full object-cover" /> :
                      <div className="w-full h-full flex items-center justify-center"><Car className="w-6 h-6 text-muted-foreground" /></div>}
                    <div className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded bg-black/70 text-white text-[10px] font-mono">{j.days_in_shop}d</div>
                    <div className="absolute top-1.5 left-1.5 flex gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                      <button
                        type="button"
                        draggable={false}
                        onDragStart={(e) => e.stopPropagation()}
                        onClick={(e) => { e.stopPropagation(); openInspection(j.id); }}
                        disabled={openingInspection === j.id}
                        className="w-6 h-6 rounded bg-black/70 hover:bg-primary text-white flex items-center justify-center disabled:opacity-50"
                        title="Ver inspeção"
                        data-testid={`kanban-inspection-${j.id}`}
                      >
                        <ClipboardCheck className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        draggable={false}
                        onDragStart={(e) => e.stopPropagation()}
                        onClick={(e) => { e.stopPropagation(); setEditJobId(j.id); }}
                        className="w-6 h-6 rounded bg-black/70 hover:bg-black text-white flex items-center justify-center"
                        title="Editar veículo e cliente"
                        data-testid={`kanban-edit-${j.id}`}
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        draggable={false}
                        onDragStart={(e) => e.stopPropagation()}
                        onClick={(e) => { e.stopPropagation(); setMoveJob(j); }}
                        className="w-6 h-6 rounded bg-black/70 hover:bg-foreground hover:text-background text-white flex items-center justify-center"
                        title="Mover para outro Kanban"
                        data-testid={`kanban-move-${j.id}`}
                      >
                        <Send className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        draggable={false}
                        onDragStart={(e) => e.stopPropagation()}
                        onClick={(e) => { e.stopPropagation(); setArchiveJob(j); }}
                        className="w-6 h-6 rounded bg-black/70 hover:bg-foreground hover:text-background text-white flex items-center justify-center"
                        title="Arquivar (mantém histórico)"
                        data-testid={`kanban-archive-${j.id}`}
                      >
                        <Archive className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        draggable={false}
                        onDragStart={(e) => e.stopPropagation()}
                        onClick={(e) => { e.stopPropagation(); setConfirmDelJob(j); }}
                        className="w-6 h-6 rounded bg-black/70 hover:bg-red-600 text-white flex items-center justify-center"
                        title="Remover do fluxo"
                        data-testid={`kanban-delete-${j.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  <div className="p-2.5">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-display font-semibold text-[13px] leading-tight truncate">{j.vehicle?.year} {j.vehicle?.make} {j.vehicle?.model}</div>
                        <div className="text-[11px] text-muted-foreground truncate">{j.client_name}</div>
                      </div>
                      {priorityStyle && (
                        <span className={`text-[9px] uppercase tracking-widest font-bold shrink-0 flex items-center gap-0.5 ${priorityStyle.text}`}>
                          <AlertCircle className="w-2.5 h-2.5" />
                          {priorityStyle.label}
                        </span>
                      )}
                    </div>
                    <div className="mt-1.5 flex items-center justify-between text-[10px]">
                      <div className="truncate text-muted-foreground">
                        {j.vehicle?.plate ? <span className="font-mono">{j.vehicle.plate}</span> : "—"}
                      </div>
                      <div className="flex items-center gap-1 font-mono shrink-0">
                        <UserIcon className="w-2.5 h-2.5" />
                        {j.technician?.split(" ")[0] || "—"}
                      </div>
                    </div>
                    {j.notes && (
                      <div className="mt-1.5 text-[10.5px] text-muted-foreground line-clamp-2 border-l-2 border-border pl-1.5 italic">
                        {j.notes}
                      </div>
                    )}
                    {j.delivered_at && (
                      <div
                        className="mt-2 inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border border-border bg-secondary/60"
                        title={new Date(j.delivered_at).toLocaleString("pt-BR")}
                        data-testid={`kanban-delivered-${j.id}`}
                      >
                        <Package className="w-2.5 h-2.5" />
                        Entregue em {new Date(j.delivered_at).toLocaleDateString("pt-BR")}
                      </div>
                    )}
                    <button
                      type="button"
                      draggable={false}
                      onDragStart={(e) => e.stopPropagation()}
                      onClick={(e) => { e.stopPropagation(); openInspection(j.id); }}
                      disabled={openingInspection === j.id}
                      className="mt-2 w-full inline-flex items-center justify-center gap-1 text-[11px] font-medium py-1 rounded border border-border hover:border-primary hover:text-primary transition disabled:opacity-50"
                      data-testid={`kanban-ver-inspecao-${j.id}`}
                    >
                      <ClipboardCheck className="w-3 h-3" />
                      {openingInspection === j.id ? "Abrindo…" : "Ver Inspeção"}
                    </button>
                    {renderCardExtra && renderCardExtra(j)}
                  </div>
                </div>
                );
              })}
              {colJobs.length === 0 && (
                <div className="text-center text-[11px] text-muted-foreground py-8 border border-dashed border-border rounded">Vazio</div>
              )}
            </div>
          </div>
        );
      })}

      {/* Dialog UNIFICADO — edita veículo, cliente e dados operacionais em UMA fonte de verdade */}
      <EditarVeiculoClienteDialog
        jobId={editJobId}
        open={!!editJobId}
        onOpenChange={(v) => !v && setEditJobId(null)}
        onSaved={() => { onJobsChanged && onJobsChanged(); }}
      />

      {/* Confirm delete */}
      <Dialog open={!!confirmDelJob} onOpenChange={(v) => !v && setConfirmDelJob(null)}>
        <DialogContent data-testid="kanban-delete-dialog">
          <DialogHeader>
            <DialogTitle>Remover do fluxo</DialogTitle>
            <DialogDescription>
              O veículo <b>{confirmDelJob?.vehicle?.year} {confirmDelJob?.vehicle?.make} {confirmDelJob?.vehicle?.model}</b> será removido do Kanban.
              O cadastro do veículo e do cliente <b>não</b> serão excluídos — apenas o registro do atendimento atual.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmDelJob(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={deleteJob} data-testid="kanban-delete-confirm">
              <Trash2 className="w-4 h-4 mr-1.5" /> Remover do fluxo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mover Kanban dialog */}
      <MoveKanbanDialog
        job={moveJob}
        moving={moving}
        onOpenChange={(v) => !v && setMoveJob(null)}
        onConfirm={async (target) => {
          setMoving(true);
          try {
            await api.post(`/jobs/${moveJob.id}/move-kanban`, { target_job_type: target });
            const labels = { mechanic: "Kanban Oficina", body_shop: "Body Shop", warranty: "Garantia Estendida" };
            toast.success(`OS movida para ${labels[target]}`);
            setMoveJob(null);
            onJobsChanged && onJobsChanged();
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro ao mover");
          } finally { setMoving(false); }
        }}
      />

      {/* Arquivar dialog */}
      <Dialog open={!!archiveJob} onOpenChange={(v) => !v && setArchiveJob(null)}>
        <DialogContent data-testid="kanban-archive-dialog">
          <DialogHeader>
            <DialogTitle>Arquivar veículo</DialogTitle>
            <DialogDescription>
              O veículo <b>{archiveJob?.vehicle?.year} {archiveJob?.vehicle?.make} {archiveJob?.vehicle?.model}</b> será removido do Kanban, mas <b>todo o histórico</b> (fotos, inspeção, notas, OS) será preservado em <b>Arquivados</b>. Você pode restaurá-lo a qualquer momento.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setArchiveJob(null)}>Cancelar</Button>
            <Button
              onClick={async () => {
                setArchiving(true);
                try {
                  await api.post(`/jobs/${archiveJob.id}/archive`);
                  toast.success("Veículo arquivado");
                  setArchiveJob(null);
                  onJobsChanged && onJobsChanged();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Erro ao arquivar");
                } finally { setArchiving(false); }
              }}
              disabled={archiving}
              data-testid="kanban-archive-confirm"
            >
              <Archive className="w-4 h-4 mr-1.5" /> {archiving ? "Arquivando…" : "Arquivar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- MoveKanbanDialog ----------
const KANBAN_TARGETS = [
  { value: "mechanic", label: "Kanban Oficina", desc: "Fluxo mecânico padrão (Entrou → Pronto → Entregue)" },
  { value: "body_shop", label: "Kanban Body Shop", desc: "Fluxo de funilaria e pintura" },
  { value: "warranty", label: "Kanban Garantia Estendida", desc: "Fluxo de garantia com estimate, kit e aprovação" },
];

function MoveKanbanDialog({ job, moving, onOpenChange, onConfirm }) {
  const [target, setTarget] = useState("");

  useEffect(() => {
    if (job) setTarget("");
  }, [job]);

  if (!job) return null;
  const currentType = job.job_type || "mechanic";
  const available = KANBAN_TARGETS.filter((t) => t.value !== currentType);

  return (
    <Dialog open={!!job} onOpenChange={onOpenChange}>
      <DialogContent data-testid="kanban-move-dialog">
        <DialogHeader>
          <DialogTitle>Mover para outro Kanban</DialogTitle>
          <DialogDescription>
            A mesma OS será transferida — todo o histórico (inspeção, fotos, notas) é preservado. O status volta para a primeira coluna do novo Kanban.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <div className="text-xs text-muted-foreground bg-secondary/50 rounded p-3">
            <div className="font-medium text-foreground">
              {job.vehicle?.year} {job.vehicle?.make} {job.vehicle?.model} · {job.vehicle?.plate || "—"}
            </div>
            <div className="mt-0.5">Cliente: {job.client_name}</div>
            <div className="mt-0.5">
              Kanban atual: <b className="text-foreground">{KANBAN_TARGETS.find((t) => t.value === currentType)?.label || currentType}</b>
            </div>
          </div>
          <div className="space-y-2 pt-2">
            {available.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => setTarget(t.value)}
                className={`w-full text-left p-3 rounded-md border transition-colors ${target === t.value ? "border-foreground bg-secondary" : "border-border hover:border-foreground/40"}`}
                data-testid={`kanban-move-target-${t.value}`}
              >
                <div className="font-medium text-sm">{t.label}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">{t.desc}</div>
              </button>
            ))}
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            onClick={() => target && onConfirm(target)}
            disabled={!target || moving}
            data-testid="kanban-move-confirm"
          >
            {moving ? "Movendo…" : "Confirmar transferência"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
